/**
 * Create-menu-plan dialog (issue #156 sub-PR #4).
 *
 * Bespoke create flow because the generic `ResourceFormDialog`
 * isn't a good fit:
 *   - the only input is `week_start` (a single date field), so a
 *     dialog smaller than the generic form's 2-column layout reads
 *     better;
 *   - INV-M2-1 collision needs operator-friendly UX (toast the
 *     `extras.week_collision` flag + display the conflicting
 *     week_start), not the generic `resource.createFailed` fallback;
 *   - on success, navigate the operator straight to the plan's
 *     detail page where they fill in the items — saves the "where
 *     did my new plan go" friction step.
 *
 * Monday validation is client-side: `week_start` MUST be an ISO
 * Monday per the M2 service's `MENU_PLAN_WEEK_START_REGEX` invariant
 * (one live plan per week starts on Monday). Pre-validating saves
 * a round trip and surfaces the constraint at the click site.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Modal,
  ModalBody,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Button,
  DatePicker,
  Label,
} from '@resource-kit/ui/react';
import { useToast } from '@components/ui/Toast';
import { menuPlanAPI } from '@/services';
import { queryKeys } from '@lib/queryKeys';
import { ApiError } from '@lib/api';

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Returns true iff `yyyy-mm-dd` corresponds to a Monday in UTC.
 *
 *  Backend enforces this via INV-M2-1's `week_start` partial UNIQUE
 *  + the service-layer `week_start` zod regex. Pre-validating on the
 *  frontend gives the operator immediate feedback instead of a
 *  400-after-submit. UTC interpretation matches `new Date('YYYY-MM-DD')`
 *  semantics (parsed as midnight UTC). */
function isIsoMonday(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return false;
  return d.getUTCDay() === 1;
}

export function CreateMenuPlanDialog({ open, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [weekStart, setWeekStart] = useState<string>('');
  const [localError, setLocalError] = useState<string | null>(null);

  const reset = () => {
    setWeekStart('');
    setLocalError(null);
  };

  const handleClose = () => {
    if (mutation.isPending) return;
    reset();
    onClose();
  };

  const mutation = useMutation({
    mutationFn: () => menuPlanAPI.createDraft({ week_start: weekStart }),
    onSuccess: (plan) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.menuPlans.lists() });
      toast.success(t('menu_plan.create.success'));
      reset();
      onClose();
      // `createDraft` resolves the unwrapped `MenuPlan` (ADR-0006 §5 —
      // `http` strips the `{ data }` envelope). Navigate to the new
      // plan's detail so the operator can immediately start adding
      // items. Generic ResourcePage's create flow lacks this step.
      navigate(`/menu-planning/plans/${plan.id}`);
    },
    onError: (e) => {
      // The rebuilt backend signals INV-M2-1 week-collision as a 409
      // with a human `message` ("A live plan already exists for this
      // week") and no structured `extras` sentinel, so surface the
      // server message directly.
      if (e instanceof ApiError) {
        toast.error(e.message || t('menu_plan.create.error.unknown'));
        return;
      }
      toast.error(t('menu_plan.create.error.unknown'));
    },
  });

  const handleSubmit = () => {
    setLocalError(null);
    if (!weekStart) {
      setLocalError(t('menu_plan.create.error.required'));
      return;
    }
    if (!isIsoMonday(weekStart)) {
      // EQD §3 — fail loud at the boundary. The error message
      // tells the operator exactly what's wrong (it's not Monday)
      // rather than silently rounding to the nearest Monday or
      // sending the bad value to the backend.
      setLocalError(t('menu_plan.create.error.notMonday'));
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal isOpen={open} onClose={handleClose} size="sm">
      <ModalHeader onClose={handleClose}>
        <ModalTitle>{t('menu_plan.create.title')}</ModalTitle>
        <ModalDescription>{t('menu_plan.create.description')}</ModalDescription>
      </ModalHeader>
      <ModalBody>
        <div className="space-y-2">
          <Label htmlFor="week-start">{t('menu_plan.field.weekStart')}</Label>
          <DatePicker
            value={weekStart}
            onChange={(v) => {
              setWeekStart(v);
              setLocalError(null);
            }}
            placeholder={t('menu_plan.create.weekStartPlaceholder')}
            data-testid="menu-plan-create-week-start"
          />
          <p className="text-xs text-muted-foreground">{t('menu_plan.create.weekStartHint')}</p>
          {localError && (
            <p className="text-sm text-destructive" role="alert">
              {localError}
            </p>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={handleClose} disabled={mutation.isPending}>
          {t('ui.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={mutation.isPending}
          data-testid="menu-plan-create-submit"
        >
          {mutation.isPending ? t('menu_plan.create.submitting') : t('menu_plan.create.submit')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// Exported for unit tests — the Monday-check is a closed predicate
// the M2 service relies on, so a regression test for it is
// load-bearing for the contract. Not consumed by other components.
export { isIsoMonday };
